import type { DocEntry } from "@manifest/schema.ts";
import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";

interface Props {
  docs: DocEntry[];
  open: boolean;
  onClose: () => void;
  onSelect: (doc: DocEntry) => void;
}

const RESULT_LIMIT = 8;

export function SearchOverlay({ docs, open, onClose, onSelect }: Props) {
  const [query, setQuery] = useState("");

  const fuse = useMemo(
    () =>
      new Fuse(docs, {
        keys: ["title", "id"],
        threshold: 0.4,
        includeScore: false,
      }),
    [docs],
  );

  const results = query
    ? fuse
        .search(query)
        .map((r) => r.item)
        .slice(0, RESULT_LIMIT)
    : docs.slice(0, RESULT_LIMIT);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Reset query when overlay reopens
  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 bg-ink-950/95" onClick={onClose} role="presentation">
      <div
        className="max-w-2xl mx-auto mt-32 bg-ink-900 border border-ink-800 rounded-lg shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Search RFCs"
      >
        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search RFCs by title or id…"
          className="w-full p-4 bg-transparent border-b border-ink-800 text-ink-100 outline-none focus:border-chain-400"
        />
        <div className="max-h-96 overflow-y-auto">
          {results.length === 0 && (
            <div className="px-4 py-3 text-ink-200/60 italic">no matches</div>
          )}
          {results.map((doc) => (
            <button
              key={doc.id}
              type="button"
              onClick={() => {
                onSelect(doc);
                onClose();
              }}
              className="w-full text-left px-4 py-3 hover:bg-ink-800 border-b border-ink-800/30 last:border-b-0"
            >
              <div className="font-serif">{doc.title}</div>
              <div className="text-xs text-ink-200/50 font-mono">
                {doc.cluster} · slot {doc.slot}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
